import React, { useState, useEffect } from 'react';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import dayjs from 'dayjs';
import NoDataFound from '$/components/global/noData';
import { AgGridReact } from 'ag-grid-react';
import './index.scss';
import CloSummary from '$/pages/client/clo-analytics/clo-detail/CloSummary';
import NotFound from '$/pages/not-found';
import { CloService } from '$/services';
import Loading from '$/components/loading/Loading';
interface ICloOverviewProps {
  isLoading: boolean;
  featureFlags?: string[];
  currentClo?: Bixby.Models.Clo;
  heatMapActive: boolean;
}

const CloOverview: React.FC<ICloOverviewProps> = props => {
  const { featureFlags, currentClo, heatMapActive } = props;
  const [cloAssets, setCloAssests] = useState([] as Bixby.Models.CloAssetDetails[]);
  const [dataForGrid, setDataForGrid] = useState([] as Bixby.Models.CloAnalytics[]);
  const [isLoading, setIsLoading] = useState(null as boolean | null);
  const [dataRetrievalComplete, setDataRetrievalComplete] = useState(false as boolean);
  const [dataError, setDataError] = useState(undefined as string | undefined);

  useEffect(() => {
    async function getAnalytics() {
      try {
        if (!currentClo) return;
        setIsLoading(true);
        const result: Bixby.Models.CloAnalytics[] = await CloService.getSummaryAnalyticsById(currentClo?.id);
        setDataForGrid(result || []);
      } catch (err: any) {
        setDataError('Error retrieving data');
      } finally {
        setIsLoading(false);
        setDataRetrievalComplete(true);
      }
    }
    if (featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
      getAnalytics();
    }
  }, [currentClo, featureFlags]);

  useEffect(() => {
    function getAssets() {
      if (currentClo && currentClo.snapshots.length > 0) {
        const snapshots: Bixby.Models.CloSnapshot[] = Bixby.Utils.orderBy(
          currentClo.snapshots,
          _ => _.asOfDate,
          'desc'
        );
        if (snapshots !== null) {
          setCloAssests(snapshots[0].assets);
        }
      }
    }
    if (featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
      getAssets();
    }
  }, [currentClo, featureFlags]);

  const formatBasePoint = (value: number) => {
    const basePoint = value * 10000;
    return `${basePoint.toPrecision(3)}`;
  };

  const formatNumberCommas = (value: number) => {
    if (value >= 0) {
      return value.toLocaleString();
    } else {
      return 'N/A';
    }
  };

  if (!featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
    return <NotFound />;
  }
  if (!Bixby.Utils.isNullOrEmpty(dataError)) {
    return (
      <React.Fragment>
        <section className="analytics-section">
          <div className="analytics-error-section">
            <p className="error">{dataError}</p>
          </div>
        </section>
      </React.Fragment>
    );
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <React.Fragment>
        <section className="clo_overview">
          <div className="capital_wrap">
            {currentClo && dataForGrid && <CloSummary dataForGrid={dataForGrid} heatMapActive={heatMapActive} />}
          </div>
        </section>
        {cloAssets && !Bixby.Utils.isNullOrEmpty(cloAssets) ? (
          <section className="clo_overview">
            <div className="capital_wrap">
              <div className="top_wrap">
                <h2 className="heading_title">
                  Assets Breakdown <></>
                  <span className="sub_heading_title">
                    (Notional: $
                    {(currentClo?.snapshots?.length ?? -1 > 0) &&
                      currentClo?.snapshots[0].notional &&
                      formatNumberCommas(currentClo?.snapshots[0].notional)}
                    )
                  </span>
                </h2>
              </div>
            </div>
            <div className="assets_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={true}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-center',
                    }}
                    rowData={cloAssets}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    columnDefs={[
                      {
                        field: 'assetName',
                        headerName: 'Asset',
                        minWidth: 200,
                        pinned: 'left',
                        cellClass: 'ag-cell-left',
                      },
                      { field: 'assetCount', headerName: 'CLO Count', minWidth: 125 },
                      {
                        field: 'notional',
                        headerName: 'Notional',
                        minWidth: 150,
                        valueFormatter: params => formatNumberCommas(params.value),
                      },
                      {
                        field: 'spread',
                        headerName: 'Spread',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: params => (params.value ? formatBasePoint(params.value) : 'NA'),
                      },
                      {
                        field: 'maturityDate',
                        headerName: 'Maturity',
                        minWidth: 125,
                        valueFormatter: params =>
                          params.value ? (Bixby.Utils.formatMonthYear(dayjs.utc(params.value).toDate()) as any) : 'NA',
                      },
                      { field: 'industry', headerName: 'Fitch Industry', minWidth: 250, cellClass: 'ag-cell-left' },
                      {
                        field: 'figi',
                        headerName: 'Figi',
                        minWidth: 150,
                        valueFormatter: params => (params.value ? params.value : 'NA'),
                      },
                      {
                        field: 'pmtFreq',
                        headerName: 'Freq',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: params => (params.value ? params.value : 'NA'),
                      },
                      {
                        field: 'dealRegions',
                        headerName: 'Region',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: params => (params.value ? params.value : 'NA'),
                      },
                      {
                        field: 'country',
                        headerName: 'Country',
                        minWidth: 150,
                        valueFormatter: params => (params.value ? params.value : 'NA'),
                      },
                      {
                        field: 'couponType',
                        headerName: 'Coupon Type',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: params => (params.value ? params.value : 'NA'),
                      },
                    ]}></AgGridReact>
                </div>
              </div>
              <p className="clo-note">Source: CLO holdings provided in partnership with Fitch Group.</p>
            </div>
          </section>
        ) : dataRetrievalComplete ? (
          <section className="clo_overview">
            <div className="capital_wrap">
              <NoDataFound dataType="assets" />
            </div>
          </section>
        ) : null}
      </React.Fragment>
    );
  }
};
const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.cloState.isLoading,
    currentClo: state.cloState.currentClo,
    featureFlags: state.loginState.featureFlags,
    heatMapActive: state.cloState.heatMapActive,
  };
};

export default connect(mapStateToProps)(CloOverview);
